import { useState, useRef, useEffect } from 'react';

/**
 * VideoItem component - Displays a video with hover play functionality
 * @param {string} url - The URL of the video
 * @param {boolean} showOverlay - Whether to show the overlay with remaining count
 * @param {number} remainingCount - Number of additional media items not shown
 */
const VideoItem = ({ url, showOverlay = false, remainingCount = 0 }) => {
  const videoRef = useRef(null);
  const [duration, setDuration] = useState('0:00');
  const [currentTime, setCurrentTime] = useState('0:00');
  const [isHovering, setIsHovering] = useState(false);
  const [videoAspect, setVideoAspect] = useState(null);
  
  /**
   * Formats seconds into MM:SS format
   * @param {number} seconds - Time in seconds
   * @returns {string} - Formatted time string
   */
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };
  
  // Get video metadata (duration and aspect ratio) when video loads
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    
    const handleLoadedMetadata = () => {
      setDuration(formatTime(video.duration));
      if (video.videoWidth && video.videoHeight) {
        setVideoAspect(video.videoWidth / video.videoHeight);
      }
    };
    
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    
    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, []);
  
  // Update current time when video is playing during hover
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isHovering) return;
    
    const updateTime = () => {
      setCurrentTime(formatTime(video.currentTime));
    };
    
    const timeUpdateInterval = setInterval(updateTime, 100);
    return () => clearInterval(timeUpdateInterval);
  }, [isHovering]);
  
  /**
   * Handles mouse enter to play video
   */
  const handleMouseEnter = () => {
    if (videoRef.current) {
      videoRef.current.play().catch(e => console.log("Play failed:", e));
      setIsHovering(true);
      setCurrentTime(formatTime(videoRef.current.currentTime));
    }
  };
  
  /**
   * Handles mouse leave to pause video
   */
  const handleMouseLeave = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      setIsHovering(false);
    }
  };
  
  /**
   * Determines proper object position based on video aspect ratio
   * @returns {string} - CSS object-position value
   */
  const getObjectPosition = () => {
    if (!videoAspect) return 'center';
    return videoAspect >= 1 ? 'center' : '50% 0%';
  };
  
  return (
    <div 
      className="relative w-full h-full overflow-hidden"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Video element */}
      <video
        ref={videoRef}
        loop
        muted
        playsInline
        className="absolute w-full h-full object-cover"
        style={{ objectPosition: getObjectPosition() }}
      >
        <source src={url} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
      
      {/* Duration/timestamp display */}
      <div className="absolute bottom-2 left-2 bg-black bg-opacity-70 px-2 py-1 rounded text-white text-xs z-10">
        {isHovering ? `${currentTime} / ${duration}` : duration}
      </div>
      
      {/* Play icon overlay on hover */}
      {isHovering && (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-white z-10">
          <svg className="w-12 h-12 opacity-70" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
          </svg>
        </div>
      )}
      
      {/* Additional media count overlay */}
      {showOverlay && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-20">
          <span className="text-white font-bold text-xl">+{remainingCount}</span>
        </div>
      )}
    </div>
  );
};

export default VideoItem;